import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, DoCheck, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import * as _ from 'lodash';
import { environment } from 'src/environments/environment';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { ComponentsService } from 'src/app/template-editor/services/components.service';
import { BlueprintService } from 'src/app/template-editor/services/blueprint.service';
import { BrandingService } from 'src/app/template-editor/template-components/services/branding.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { StorageCacheBusterService } from 'src/app/template-editor/services/storage-cache-buster.service';

@Component({
    selector: 'presentation-preview',
    templateUrl: './presentation-preview.component.html',
    styleUrls: ['./presentation-preview.component.scss'],
    standalone: false
})
export class PresentationPreviewComponent implements AfterViewInit, DoCheck, OnDestroy, OnInit, OnChanges {
  // Optional inputs for multi-presentation support
  @Input() productCode?: string;
  @Input() attributeData?: any;
  @Input() disableAutoRefresh = false;
  @Input() aiContext = false;

  private DEFAULT_TEMPLATE_WIDTH = 1920;
  private DEFAULT_TEMPLATE_HEIGHT = 1080;
  private MOBILE_PREVIEW_HEIGHT = 200;
  private MOBILE_PREVIEW_HEIGHT_SHORT = 140;
  private PREVIEW_INITIAL_DELAY_MILLIS = 1000;
  private RLC_STAGING_PARAM = 'rlcStage';
  private CT_STAGING_PARAM = 'ctStage';

  private subscription: any;

  private iframeLoaded = false;
  private debounceAttributeDataTimer;
  public editorPreviewUrl;

  private componentWasSelected = false;

  private debounceResizeTimer;

  @ViewChild('templateEditorPreviewParent') iframeParent;
  @ViewChild('templateEditorPreview') iframe;

  private _getActivePresentation() {
    return this.templateEditorFactory?.presentation || {};
  }

  private _getActiveBlueprintData() {
    return this.blueprintFactory?.blueprintData || {};
  }

  private _getActiveBrandingSettings() {
    return this.brandingFactory?.brandingSettings || {};
  }

  private _getLogoComponents() {
    const blueprintData = this._getActiveBlueprintData();
    if (!blueprintData || !blueprintData.components) {
      return [];
    }

    return this.blueprintFactory.getLogoComponents();
  }

  _getPreviewHolder() {
    return this.elementRef.nativeElement.parentElement;
  }

  _getIframeParent() {
    return this.iframeParent.nativeElement;
  }

  _getIframe(): any {
    return this.iframe.nativeElement;
  }

  onResize() {
    if (this.debounceResizeTimer) {
      clearTimeout(this.debounceResizeTimer);
    }

    this.debounceResizeTimer = setTimeout(() => {
      this._applyAspectRatio();
    }, 100);
  }

  constructor(private elementRef: ElementRef,
    private sanitizer: DomSanitizer,
    private broadcaster: BroadcasterService,
    private companyStateService: CompanyStateService,
    private templateEditorFactory: TemplateEditorService,
    private componentsFactory: ComponentsService,
    private storageCacheBusterService: StorageCacheBusterService,
    private blueprintFactory: BlueprintService,
    private brandingFactory: BrandingService) {
  }

  ngOnInit(): void {
    if (!this.disableAutoRefresh) {
      this.subscription = this.broadcaster.subscribe((event: string) => {
        switch (event) {
        case 'risevision.template-editor.presentationUnsavedChanges':
        case 'risevision.template-editor.logoFileMetadata':
        case 'risevision.template-editor.storageCacheLoaded':
          this._debounceAttributeData(this._postAttributeData);
          break;
        case 'risevision.company.updated':
        case 'risevision.company.selectedCompanyChanged':
        case 'risevision.template-editor.brandingUnsavedChanges':
          setTimeout(() => {
            this._postDisplayData();
          });
          break;
        case 'risevision.aiservice.presentationChanged':
          this._setEditorPreviewUrl();
          this._postDisplayData();
          this.postAttributeDataRepeated(this.attributeData);
          break;
        default:
          return;
        }
      });
    }

    this._setEditorPreviewUrl();
  }

  ngAfterViewInit() {
    this._applyAspectRatio();
    setTimeout(this._applyAspectRatio.bind(this), this.PREVIEW_INITIAL_DELAY_MILLIS);
  }

  ngDoCheck(): void {
    if (this.componentWasSelected && !this.componentsFactory.selected) {
      this._applyAspectRatio();
    }

    this.componentWasSelected = !!this.componentsFactory.selected;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onLoad(event) {
    if (event.target.src != '') {
      this.iframeLoaded = true;

      this._postDisplayData();
      this._postAttributeData();
      this._postStartEvent();
    }
  }

  postAttributeDataRepeated(attributeData) {
    [1000, 2000, 5000].forEach(interval => {
      setTimeout(() => {
        this.postAttributeData(attributeData);
      }, interval);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attributeData']) {
      const currentValue = changes['attributeData'].currentValue;

      if (!!currentValue) {
        console.error('Posting currentValue', currentValue);

        setTimeout(() => {
          this.postAttributeDataRepeated(currentValue);
          console.error('Posted currentValue', currentValue);
        }, 1000);
      }
    }
  }

  _debounceAttributeData(callback) {
    if (this.debounceAttributeDataTimer) {
      clearTimeout(this.debounceAttributeDataTimer);
    }

    this.debounceAttributeDataTimer = setTimeout(callback.bind(this), 500);
  }

  _getWindowHref() {
    return window.location.href;
  }

  _getContentStagingParams() {
    let componentsStage = '';
    let commonStage = '';
    const parsedUrl = new URL(this._getWindowHref());

    parsedUrl.searchParams.forEach((value, key) => {
      if (key === this.RLC_STAGING_PARAM) {
        componentsStage = '&' + this.RLC_STAGING_PARAM + '=' + value;
      } else if (this.aiContext) {
        componentsStage = '&' + this.RLC_STAGING_PARAM + '=' + 20;
      }

      if (key === this.CT_STAGING_PARAM) {
        commonStage = '&' + this.CT_STAGING_PARAM + '=' + value;
      }
    });

    return componentsStage + commonStage;

  }

  _setEditorPreviewUrl() {
    if (this.productCode) {
      // Just preview the template
      const companyId = this.companyStateService.getSelectedCompanyId();
      const contentStagingParams = this._getContentStagingParams();
      const url = environment.HTML_TEMPLATE_URL.replace('PRODUCT_CODE', this.productCode) + '?type=preview&cid=' + companyId + contentStagingParams;
      this.editorPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      // Template Editor: Use presentation object
      const presentation = this._getActivePresentation();
      if (!presentation) {
        return;
      }

      const productCode = presentation.productCode;
      const presentationId = presentation.id;
      const companyId = presentation.companyId;
      const contentStagingParams = this._getContentStagingParams();

      const url = environment.HTML_TEMPLATE_URL.replace('PRODUCT_CODE', productCode) + '?type=preview&cid=' + companyId + '&presentationId=' +
        presentationId + contentStagingParams;

      this.editorPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  _getTemplateWidth() {
    const blueprintData = this._getActiveBlueprintData();
    const width = blueprintData && blueprintData.width;

    return width ? parseInt(width) : this.DEFAULT_TEMPLATE_WIDTH;
  }

  _getTemplateHeight() {
    const blueprintData = this._getActiveBlueprintData();
    const height = blueprintData && blueprintData.height;

    return height ? parseInt(height) : this.DEFAULT_TEMPLATE_HEIGHT;
  }

  _getPreviewAreaWidth() {
    const previewHolder = this._getPreviewHolder();
    return previewHolder.clientWidth;
  }

  _getPreviewAreaHeight() {
    const previewHolder = this._getPreviewHolder();
    return previewHolder.clientHeight;
  }

  _getHeightDividedByWidth() {
    return this._getTemplateHeight() / this._getTemplateWidth();
  }

  _useFullWidth() {
    const aspectRatio = this._getHeightDividedByWidth();
    const projectedHeight = this._getPreviewAreaWidth() * aspectRatio;

    return projectedHeight < this._getPreviewAreaHeight();
  }

  _getWidthFor(height) {
    const value = height / this._getHeightDividedByWidth();

    return value;
  }

  _mediaMatches(mediaQuery) {
    return window.matchMedia(mediaQuery).matches;
  }

  getMobileWidth () {
    const isShort = this._mediaMatches('(max-height: 570px)');
    const layerHeight = isShort ? this.MOBILE_PREVIEW_HEIGHT_SHORT : this.MOBILE_PREVIEW_HEIGHT;

    const value = this._getWidthFor(layerHeight);

    return value.toFixed(0);
  }

  getDesktopWidth() {
    const height = this._getPreviewAreaHeight();

    return this._getWidthFor(height).toFixed(0);
  }

  _getFrameStyle(viewSize, templateSize) {
    const ratio = (viewSize / templateSize).toFixed(4);
    const width = this._getTemplateWidth();
    const height = this._getTemplateHeight();

    return 'width: ' + width + 'px;' +
      'height: ' + height + 'px;' +
      'transform:scale3d(' + ratio + ',' + ratio + ',' + ratio + ');';
  }

  _applyAspectRatio() {
    let frameStyle;
    let parentStyle = 'height: 100%; width: 100%;';
    const isMobile = this._mediaMatches('(max-width: 768px)');

    const iframeParent = this._getIframeParent();
    const iframe = this._getIframe();

    if (isMobile) {
      const viewHeight = this._getPreviewAreaHeight();
      parentStyle = 'width: ' + this.getMobileWidth() + 'px';
      frameStyle = this._getFrameStyle(viewHeight, this._getTemplateHeight());
    } else if (this._useFullWidth()) {
      const viewWidth = this._getPreviewAreaWidth();
      frameStyle = this._getFrameStyle(viewWidth, this._getTemplateWidth());
    } else {
      const viewHeight = this._getPreviewAreaHeight();
      frameStyle = this._getFrameStyle(viewHeight, this._getTemplateHeight());
    }

    iframeParent.setAttribute('style', parentStyle);
    iframe.setAttribute('style', frameStyle);
  }

  _updateLogoData(attributeData) {
    if (attributeData && attributeData.components) {
      const logoComponents = this._getLogoComponents();
      const brandingSettings = this._getActiveBrandingSettings();

      _.each(logoComponents, (logoComponent) => {
        const component = _.find(attributeData.components, {
          id: logoComponent.id
        });

        if (component && component.isLogo !== false && brandingSettings) {
          component.metadata = brandingSettings.logoFileMetadata;
        }
      });
    }
  }

  getAttributeData() {
    return _.cloneDeep(this.templateEditorFactory.presentation.templateAttributeData);
  }

  postAttributeData(newData?) {
    const presentation = this._getActivePresentation();

    if (!newData && (!presentation || !presentation.templateAttributeData)) {
      return;
    }

    const attributeData = newData || _.cloneDeep(presentation.templateAttributeData);

    this._updateLogoData(attributeData);

    this.storageCacheBusterService.updateStorageFileTimestamps(attributeData);

    const message = {
      type: 'attributeData',
      value: attributeData
    };
    this._postMessageToTemplate(message);
  }

  _postAttributeData() {
    this.postAttributeData();
  }

  _postStartEvent() {
    this._postMessageToTemplate({
      type: 'sendStartEvent'
    });
  }

  _postDisplayData() {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    const brandingSettings = this._getActiveBrandingSettings();

    const message = {
      type: 'displayData',
      value: {
        displayAddress: {
          city: company.city,
          province: company.province,
          country: company.country,
          postalCode: company.postalCode
        },
        companyBranding: brandingSettings ? {
          logoFile: brandingSettings.logoFile,
          baseColor: brandingSettings.baseColor,
          accentColor: brandingSettings.accentColor
        } : {}
      }
    };
    this._postMessageToTemplate(message);
  }

  _postMessageToTemplate(message) {
    if (!this.iframeLoaded) {
      return;
    }

    let iframe = this._getIframe();

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(JSON.stringify(message), TemplateEditorService.HTML_TEMPLATE_DOMAIN);
    } else {
      setTimeout(() => {
        this._postMessageToTemplate(message);
      }, 1000);
    }
  }

}
